import { Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import Button from "../../../Components/Button";

const CategoryForm = ({
    register,
    handleSubmit,
    errors,
    control,
    onSubmit,
    isLoading,
    isEdit = false,
    preview,
    handleImageChange,
    isFragile,
    isHeavy,
    setValue,
}) => {
    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="pform-card">
                <div className="pform-card__head">
                    <span className="pform-card__step">1</span>
                    <div>
                        <h2 className="pform-card__title">Category Image</h2>
                        <p className="pform-card__desc">Visual icon shown to users when selecting item type</p>
                    </div>
                </div>

                <div
                    className={`pform-image-upload${preview ? " pform-image-upload--has-preview" : ""}`}
                    onClick={() => document.getElementById("category-img-input").click()}
                >
                    {preview ? (
                        <>
                            <img src={preview} alt="Category preview" />
                            <div className="pform-image-upload__overlay">
                                <i className="fas fa-camera me-2" />
                                Change Image
                            </div>
                        </>
                    ) : (
                        <div className="pform-image-upload__placeholder">
                            <div className="pform-image-upload__icon">
                                <i className="fas fa-image" />
                            </div>
                            <span className="pform-image-upload__text">Upload Category Image</span>
                            <span className="pform-image-upload__hint">Recommended: 600×400px (JPG, PNG)</span>
                        </div>
                    )}
                    <input
                        type="file"
                        id="category-img-input"
                        hidden
                        accept="image/*"
                        onChange={handleImageChange}
                    />
                </div>
            </div>

            <div className="pform-card">
                <div className="pform-card__head">
                    <span className="pform-card__step">2</span>
                    <div>
                        <h2 className="pform-card__title">Basic Details</h2>
                        <p className="pform-card__desc">Name, description, status, and weight parameters</p>
                    </div>
                </div>

                <div className="pform-field">
                    <label className="cms-field-label">
                        Category Name <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className={`cms-input ${errors.categoryName ? "is-invalid" : ""}`}
                        placeholder="e.g. Furniture, Appliances, Electronics"
                        {...register("categoryName")}
                    />
                    {errors.categoryName && (
                        <span className="cms-field-error">{errors.categoryName.message}</span>
                    )}
                </div>

                <div className="pform-field">
                    <label className="cms-field-label">
                        Short Description <span className="text-danger">*</span>
                    </label>
                    <textarea
                        className={`cms-textarea ${errors.description ? "is-invalid" : ""}`}
                        rows={3}
                        placeholder="e.g. Sofa, table, bed — items shown to users"
                        {...register("description")}
                    />
                    {errors.description && (
                        <span className="cms-field-error">{errors.description.message}</span>
                    )}
                </div>

                <div className="pform-field-row">
                    <div className="pform-field">
                        <label className="cms-field-label">
                            Status <span className="text-danger">*</span>
                        </label>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <select className={`cms-input ${errors.status ? "is-invalid" : ""}`} {...field}>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            )}
                        />
                    </div>
                    <div className="pform-field">
                        <label className="cms-field-label">
                            Est. Base Weight (lbs) <span className="text-danger">*</span>
                        </label>
                        <input
                            type="number"
                            className={`cms-input ${errors.estBaseWeight ? "is-invalid" : ""}`}
                            placeholder="50"
                            {...register("estBaseWeight")}
                        />
                        {errors.estBaseWeight && (
                            <span className="cms-field-error">{errors.estBaseWeight.message}</span>
                        )}
                    </div>
                </div>
            </div>

            <div className="pform-card">
                <div className="pform-card__head">
                    <span className="pform-card__step">3</span>
                    <div>
                        <h2 className="pform-card__title">Logistics Profile</h2>
                        <p className="pform-card__desc">Auto-apply surcharges based on item handling needs</p>
                    </div>
                </div>

                <div className="pform-toggle-grid">
                    <div
                        className={`pform-toggle-card ${isFragile ? "is-active--fragile" : ""}`}
                        onClick={() => setValue("isFragile", !isFragile)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && setValue("isFragile", !isFragile)}
                    >
                        <div className="pform-toggle-card__head">
                            <div className="pform-toggle-card__icon pform-toggle-card__icon--fragile">
                                <i className="fas fa-wine-glass-alt" />
                            </div>
                            <label className="pform-switch" onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={isFragile}
                                    onChange={(e) => setValue("isFragile", e.target.checked)}
                                />
                                <span className="pform-switch__slider" />
                            </label>
                        </div>
                        <h3 className="pform-toggle-card__title">Fragile Item</h3>
                        <p className="pform-toggle-card__hint">Auto-applies handling surcharge</p>
                    </div>

                    <div
                        className={`pform-toggle-card ${isHeavy ? "is-active--heavy" : ""}`}
                        onClick={() => setValue("isHeavy", !isHeavy)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && setValue("isHeavy", !isHeavy)}
                    >
                        <div className="pform-toggle-card__head">
                            <div className="pform-toggle-card__icon pform-toggle-card__icon--heavy">
                                <i className="fas fa-dumbbell" />
                            </div>
                            <label className="pform-switch pform-switch--heavy" onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="checkbox"
                                    checked={isHeavy}
                                    onChange={(e) => setValue("isHeavy", e.target.checked)}
                                />
                                <span className="pform-switch__slider" />
                            </label>
                        </div>
                        <h3 className="pform-toggle-card__title">Heavy (Helper Req.)</h3>
                        <p className="pform-toggle-card__hint">Auto-applies standard helper fee</p>
                    </div>
                </div>
            </div>

            <div className="pform-footer">
                <Link to="/category-management" className="cms-btn-cancel">
                    Cancel
                </Link>
                <Button className="cms-btn-save" type="submit" loading={isLoading}>
                    {isEdit ? "Update Category" : "Save Category"}
                </Button>
            </div>
        </form>
    );
};

export default CategoryForm;
